import { useCallback, useMemo } from 'react'
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query'
import { useGlobalEvent } from '@highlight/ipc'
import { getChatApiRoute } from '@renderer/chat/api/v1Client'
import { useAuth } from '@renderer/chat/providers'
import { weakMemoize } from '@renderer/utils/weak-memoize'
import type { RecentActivity, RecentActivityType } from '@renderer/overlay/Sidepanel/types'
import { HISTORY_ACTIVITY_QUERY_KEY } from '@renderer/stores/conversation-query-keys'

// One page size for every consumer so the home preview and the drawer share a
// single query cache per filter — the drawer then opens on already-fetched data.
const HISTORY_PAGE_SIZE = 25

export type HistoryFilter = 'all' | RecentActivityType

// Gateway HistoryItemSchema (lib/schemas/history.ts in http-gateway).
interface HistoryApiItem {
  id: string
  type: RecentActivityType
  title?: string
  updatedAt: string
  actionType?: string
  toolSet?: string
  previewRevision?: number
  previewAcknowledgedRevision?: number
  metadata: {
    braintrust_link?: string
    meeting_source?: string
  }
}

interface HistoryApiResponse {
  success: boolean
  data: Array<HistoryApiItem>
  nextCursor?: string
}

interface HistoryPage {
  data: Array<RecentActivity>
  nextCursor?: string
}

const EMPTY_HISTORY_ITEMS: Array<RecentActivity> = []

class HistoryRequestError extends Error {
  constructor(readonly status: number) {
    super(`Failed to fetch history (${status})`)
  }
}

const toHistoryActivityItem = weakMemoize(
  (item: HistoryApiItem): RecentActivity => ({
    id: item.id,
    type: item.type,
    title: item.title,
    unifiedUpdatedAt: new Date(item.updatedAt),
    meetingSource: item.metadata.meeting_source,
    actionType: item.actionType,
    toolSet: item.toolSet,
    braintrustLink: item.metadata.braintrust_link,
    previewRevision: item.previewRevision,
    previewAcknowledgedRevision: item.previewAcknowledgedRevision,
  }),
)

export function historyFilterToType(filter: HistoryFilter): RecentActivityType | undefined {
  return filter === 'all' ? undefined : filter
}

async function fetchHistoryPage({
  getAuthToken,
  type,
  nextCursor,
}: {
  getAuthToken: () => Promise<string>
  type?: RecentActivityType
  nextCursor?: string
}): Promise<HistoryPage> {
  const params = new URLSearchParams({ limit: String(HISTORY_PAGE_SIZE) })
  if (type) {
    params.set('type', type)
  }
  if (nextCursor) {
    params.set('nextCursor', nextCursor)
  }

  const response = await fetch(getChatApiRoute(`api/v1/history?${params.toString()}`), {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${await getAuthToken()}`,
    },
  })

  if (!response.ok) {
    throw new HistoryRequestError(response.status)
  }

  const json = (await response.json()) as HistoryApiResponse
  return {
    data: json.data.map(toHistoryActivityItem),
    nextCursor: json.nextCursor,
  }
}

/**
 * Invalidates the history feed when its underlying records change in any renderer.
 * Mount ONCE per renderer (SidepanelOverlay) — registering these inside
 * useHistoryActivity would add one copy per consumer, and each duplicate
 * invalidation cancels and restarts the refetch the previous one just started.
 */
export function useHistoryActivityInvalidation(): void {
  const queryClient = useQueryClient()
  const invalidateHistoryActivity = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: [HISTORY_ACTIVITY_QUERY_KEY] })
  }, [queryClient])

  useGlobalEvent('chat:history:invalidate', invalidateHistoryActivity)
  useGlobalEvent('chat:message:sent', invalidateHistoryActivity)
  useGlobalEvent('chat:created', invalidateHistoryActivity)
  // Renderer-emitted on update/delete; main-emitted on persist/summary respectively.
  useGlobalEvent('audio-notes:changed', invalidateHistoryActivity)
  useGlobalEvent('audio-note:saved', invalidateHistoryActivity)
  useGlobalEvent('audio-note:summary-ready', invalidateHistoryActivity)
}

function historyActivityQueryOptions(getAuthToken: () => Promise<string>, type?: RecentActivityType) {
  return {
    queryKey: [HISTORY_ACTIVITY_QUERY_KEY, type ?? 'all'],
    queryFn: ({ pageParam }: { pageParam: string }) =>
      fetchHistoryPage({
        getAuthToken,
        type,
        nextCursor: pageParam || undefined,
      }),
    initialPageParam: '',
    getNextPageParam: (lastPage: HistoryPage) => lastPage.nextCursor,
    staleTime: 60 * 1000,
    // Default count/backoff, but a 4xx (e.g. a rejected cursor) won't succeed on retry.
    retry: (failureCount: number, error: Error) =>
      failureCount < 3 && !(error instanceof HistoryRequestError && error.status < 500),
  }
}

/**
 * Whether the account has any history at all (unfiltered). Shares the 'all'
 * feed's query cache; `select` collapses the pages to a boolean so consumers
 * only re-render when presence flips — not on every feed change. `undefined`
 * while the first fetch is still in flight.
 */
export function useHasHistoryActivity(): boolean | undefined {
  const { getAuthToken } = useAuth()

  const query = useInfiniteQuery({
    ...historyActivityQueryOptions(getAuthToken),
    select: (data) => data.pages.some((page) => page.data.length > 0),
  })

  return query.data
}

export function useHistoryActivity({ type }: { type?: RecentActivityType }) {
  const { getAuthToken } = useAuth()

  const query = useInfiniteQuery(historyActivityQueryOptions(getAuthToken, type))

  const items = useMemo(() => query.data?.pages.flatMap((page) => page.data) ?? EMPTY_HISTORY_ITEMS, [query.data])

  return {
    items,
    isLoading: query.isLoading,
    isFetchingNextPage: query.isFetchingNextPage,
    isFetchNextPageError: query.isFetchNextPageError,
    hasNextPage: query.hasNextPage,
    fetchNextPage: query.fetchNextPage,
  }
}
